import { useEffect } from "react";
import { X, Lock } from "lucide-react";

/**
 * RecordDrawer · slides in from the right and displays the underlying
 * system-of-record view for a reasoning node. Read-only, mono-formatted,
 * framed as "we opened the SAP/MES/mailbox record" rather than another
 * product screen. Answers the "no black box" objection in one click.
 */
export default function RecordDrawer({ open, record, onClose }) {
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handler);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", handler);
      document.body.style.overflow = "";
    };
  }, [open, onClose]);

  if (!open || !record) return null;

  return (
    <div className="fixed inset-0 z-50" data-testid="record-drawer">
      {/* Backdrop */}
      <div
        className="absolute inset-0"
        style={{ background: "rgba(15, 23, 42, 0.35)", animation: "mos-fade-in 180ms ease-out both" }}
        onClick={onClose}
      />

      {/* Drawer */}
      <div
        className="absolute right-0 top-0 bottom-0 w-[640px] shadow-2xl flex flex-col"
        style={{
          background: "var(--mos-surface)",
          animation: "mos-drawer-in 260ms cubic-bezier(0.22, 1, 0.36, 1) both",
        }}
      >
        {/* Header */}
        <div
          className="px-6 py-4 border-b flex items-start justify-between gap-4"
          style={{ borderColor: "var(--mos-border)", background: "#FAFBFC" }}
        >
          <div>
            <div className="text-[10.5px] tracking-[0.22em] uppercase text-slate-500 mb-1 flex items-center gap-2">
              <Lock size={10} />
              System of record · read only
            </div>
            <div className="flex items-baseline gap-3">
              <span className="mos-mono text-[13px] font-semibold text-slate-900">
                {record.system}
              </span>
              <span className="text-slate-300">·</span>
              <span className="mos-mono text-[13px] text-slate-700">
                {record.recordId}
              </span>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="mos-btn mos-btn-ghost -mr-2"
            aria-label="Close record"
            data-testid="btn-close-drawer"
          >
            <X size={16} />
          </button>
        </div>

        {/* Body · scrolls independently */}
        <div className="flex-1 overflow-y-auto">
          {record.key === "email" && <EmailBody record={record} />}
          {record.key === "po" && <POBody record={record} />}
          {record.key === "wo" && <WOBody record={record} />}
          {record.key === "so" && <SOBody record={record} />}
        </div>

        {/* Footer · SP-04 reminder */}
        <div
          className="px-6 py-3 border-t text-[11.5px] text-slate-500"
          style={{ borderColor: "var(--mos-border)", background: "#FAFBFC" }}
        >
          Displayed via read connector. No writes have been made from this view.
        </div>
      </div>
    </div>
  );
}

// ── Field grid ──────────────────────────────────────────────────────────────

function FieldGrid({ fields }) {
  return (
    <div className="grid grid-cols-2 gap-x-6 gap-y-3">
      {fields.map((f) => (
        <div key={f.label}>
          <div className="text-[10.5px] uppercase tracking-wider text-slate-500 mb-0.5">
            {f.label}
          </div>
          <div
            className={"text-[13px] " + (f.mono ? "mos-mono font-medium" : "font-medium")}
            style={{ color: "var(--mos-text)" }}
          >
            {f.value}
          </div>
        </div>
      ))}
    </div>
  );
}

function SectionTitle({ children }) {
  return (
    <div className="text-[10.5px] tracking-[0.18em] uppercase text-slate-500 font-semibold mb-3 pt-1">
      {children}
    </div>
  );
}

// ── Email ───────────────────────────────────────────────────────────────────

function EmailBody({ record }) {
  return (
    <div className="p-6 space-y-6">
      <div>
        <SectionTitle>Message metadata</SectionTitle>
        <FieldGrid fields={record.fields} />
      </div>

      <div className="pt-2">
        <SectionTitle>Message body</SectionTitle>
        <div
          className="rounded-md border p-5 text-[13.5px] leading-relaxed space-y-3"
          style={{
            background: "var(--mos-surface-2)",
            borderColor: "var(--mos-border)",
            color: "var(--mos-text)",
          }}
        >
          {record.body.map((b, i) => {
            if (b.kind === "highlight") {
              return (
                <p
                  key={i}
                  className="rounded px-3 py-2 border-l-2"
                  style={{
                    background: "var(--mos-amber-bg)",
                    borderColor: "var(--mos-amber)",
                    color: "#78350F",
                  }}
                  data-testid="drawer-email-highlight"
                >
                  {b.text}
                </p>
              );
            }
            if (b.kind === "signature") {
              return (
                <p key={i} className="text-slate-500 pt-2 border-t" style={{ borderColor: "var(--mos-border)" }}>
                  {b.text}
                </p>
              );
            }
            return (
              <p key={i} className="text-slate-700">
                {b.text}
              </p>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ── Purchase order ──────────────────────────────────────────────────────────

function POBody({ record }) {
  return (
    <div className="p-6 space-y-6">
      <div>
        <SectionTitle>Header</SectionTitle>
        <FieldGrid fields={record.fields} />
      </div>

      <div>
        <SectionTitle>Line items</SectionTitle>
        <LineItemsTable
          items={record.lineItems}
          columns={[
            { key: "line", label: "Line", mono: true, w: "50px" },
            { key: "partNumber", label: "Part", mono: true },
            { key: "description", label: "Description" },
            { key: "qty", label: "Qty", mono: true, w: "70px" },
            { key: "unitPrice", label: "Unit price", mono: true, w: "88px" },
            { key: "promised", label: "Promised", mono: true, w: "110px" },
          ]}
        />
      </div>

      <div>
        <SectionTitle>Revision history</SectionTitle>
        <div className="space-y-2.5">
          {record.revisions.map((r) => (
            <div
              key={r.rev}
              className="flex items-start gap-4 rounded-md border p-3"
              style={{ borderColor: "var(--mos-border)", background: "var(--mos-surface-2)" }}
            >
              <span className="mos-mono text-[11.5px] font-semibold text-slate-900 flex-none">
                rev {r.rev}
              </span>
              <span className="mos-mono text-[11.5px] text-slate-600 flex-none">
                {r.date}
              </span>
              <span className="text-[11.5px] text-slate-500 flex-none">by</span>
              <span className="text-[11.5px] text-slate-800 flex-none min-w-[110px]">
                {r.by}
              </span>
              <span className="flex-1 text-[12.5px] text-slate-700 leading-snug">
                {r.change}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── Work order ──────────────────────────────────────────────────────────────

function WOBody({ record }) {
  return (
    <div className="p-6 space-y-6">
      <div>
        <SectionTitle>Header</SectionTitle>
        <FieldGrid fields={record.fields} />
      </div>

      <div>
        <SectionTitle>Routing · operations</SectionTitle>
        <LineItemsTable
          items={record.routing}
          columns={[
            { key: "op", label: "Op", mono: true, w: "50px" },
            { key: "name", label: "Operation" },
            { key: "station", label: "Station", mono: true, w: "150px" },
          ]}
        />
      </div>

      <div>
        <SectionTitle>Material list · BOM</SectionTitle>
        <LineItemsTable
          items={record.materials}
          columns={[
            { key: "component", label: "Component" },
            { key: "qty", label: "Qty", mono: true, w: "90px" },
            { key: "status", label: "Status", w: "180px" },
          ]}
        />
      </div>
    </div>
  );
}

// ── Sales order ─────────────────────────────────────────────────────────────

function SOBody({ record }) {
  return (
    <div className="p-6 space-y-6">
      <div>
        <SectionTitle>Header</SectionTitle>
        <FieldGrid fields={record.fields} />
      </div>

      <div>
        <SectionTitle>Line items</SectionTitle>
        <LineItemsTable
          items={record.lineItems}
          columns={[
            { key: "line", label: "Line", mono: true, w: "50px" },
            { key: "partNumber", label: "Part", mono: true },
            { key: "description", label: "Description" },
            { key: "qty", label: "Qty", mono: true, w: "70px" },
            { key: "unitPrice", label: "Unit price", mono: true, w: "88px" },
            { key: "promised", label: "Promised", mono: true, w: "110px" },
          ]}
        />
      </div>

      <div>
        <SectionTitle>Contract terms</SectionTitle>
        <div
          className="rounded-md border p-4"
          style={{ borderColor: "var(--mos-border)", background: "var(--mos-surface-2)" }}
        >
          <div className="flex items-baseline gap-3 mb-2">
            <span
              className="mos-mono text-[11px] px-2 py-0.5 rounded"
              style={{ background: "var(--mos-amber-bg)", color: "var(--mos-amber)" }}
            >
              CLAUSE {record.contract.clause}
            </span>
            <span className="text-[13px] font-semibold text-slate-900">
              {record.contract.heading}
            </span>
          </div>
          <p
            className="text-[13px] text-slate-700 leading-relaxed"
            data-testid="drawer-contract-body"
          >
            {record.contract.body.split(record.contract.highlightedTerm).map((chunk, i, arr) => (
              <span key={i}>
                {chunk}
                {i < arr.length - 1 && (
                  <mark
                    style={{
                      background: "var(--mos-amber-bg)",
                      color: "#78350F",
                      padding: "0 3px",
                      borderRadius: "2px",
                    }}
                  >
                    {record.contract.highlightedTerm}
                  </mark>
                )}
              </span>
            ))}
          </p>
        </div>
      </div>

      <div>
        <SectionTitle>Delivery schedule</SectionTitle>
        <LineItemsTable
          items={record.deliverySchedule}
          columns={[
            { key: "slot", label: "Slot", w: "80px" },
            { key: "date", label: "Date", mono: true, w: "130px" },
            { key: "qty", label: "Qty", mono: true, w: "80px" },
            { key: "status", label: "Status" },
          ]}
        />
      </div>
    </div>
  );
}

function LineItemsTable({ items, columns }) {
  return (
    <div
      className="rounded-md border overflow-hidden"
      style={{ borderColor: "var(--mos-border)" }}
    >
      <table className="w-full text-left">
        <thead>
          <tr
            className="text-[10.5px] uppercase tracking-wider text-slate-500"
            style={{ background: "#FAFBFC" }}
          >
            {columns.map((c) => (
              <th
                key={c.key}
                className="py-2.5 px-3 font-semibold"
                style={c.w ? { width: c.w } : {}}
              >
                {c.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {items.map((row, i) => (
            <tr
              key={i}
              className="border-t"
              style={{ borderColor: "var(--mos-border)" }}
            >
              {columns.map((c) => (
                <td
                  key={c.key}
                  className={
                    "py-2.5 px-3 text-[12.5px] " +
                    (c.mono ? "mos-mono text-slate-900" : "text-slate-700")
                  }
                >
                  {row[c.key]}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
